<template>
  <div class="edit-user-dashboard" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'EditUserDashboard_phone.png' : 'EditUserDashboard.png'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header">
      <h1 class="page-title">Edit Your Page</h1>
    </div>

    <div class="calendar-button-wrapper">
      <button @click="goToCalendar" class="calendar-button">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </button>
    </div>

    <div class="container">
      
      <div class="content">
        <form @submit.prevent="updateUser">

          <div class="form-group">
            <label class="form-label">Full Name:</label>
            <input v-model="form.fullname" type="text" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Email:</label>
            <input v-model="form.email" type="email" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">Phone Number:</label>
            <input
              v-model="form.phone_number"
              type="text"
              class="phone-input"
              maxlength="10"
              @keypress="onPhoneKeypress"
              @input="onPhoneInput"
            />
            <p v-if="phoneError" class="phone-error">{{ phoneError }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">User Type:</label>
            <select v-model="form.user_type" class="form-input" required>
              <option disabled value="">Select</option>
              <option value="Pet Owner">Pet Owner</option>
              <option value="Pet Lover">Pet Lover</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Location</label>

            <div style="margin-top: 10px;">
              <button class="location-btn" type="button" @click="detectLocation">
                📍 Detect My Location
              </button>
            </div>

            <input
              class="form-input"
              v-model="locationDisplay"
              type="text"
              placeholder="Or enter city, place, or address"
              style="margin-top: 10px;"
              @input="onLocationInput"
              :disabled="!autoLocation"
            />
            <p v-if="locationDetected" class="location-success"></p>
            <p v-if="locationError" class="location-error">{{ locationError }}</p>
          </div>

          <button type="submit" class="update-button">Save Changes</button>
          <button type="button" class="update-button" @click="goToPage('DashboardPage')">Cancel</button>
        </form>

        <div v-if="successMessage" class="success">{{ successMessage }}</div>
        <button v-if="successMessage" class="logout-now-btn" @click="goToPage('LogoutPage')">
          Log Out Now
        </button>
        <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'EditUserDashboard',
  props: ['user', 'pets'],
  data() {
    return {
      designUrl: '/background_designs/',
      form: {
        fullname: this.user.fullname,
        email: this.user.email,
        phone_number: this.user.phone_number,
        user_type: this.user.user_type,
        location: this.user.location || '',
        location_coords: this.user.location_coords || ''
      },
      phoneError: '',
      locationDisplay: this.user.location || '',
      successMessage: '',
      errorMessage: '',
      sidebarOpen: false,
      locationDetected: false,
      locationError: '',
      petsOpen: false,
      isMobile: window.innerWidth <= 480,
      autoLocation: null,
    };
  },
  methods: {
    goToCalendar() {
      this.$emit('change-page', 'CalendarPage');
    },
    handleResize() { this.isMobile = window.innerWidth <= 480; },
    onPhoneKeypress(e) {
      if (!/\d/.test(e.key)) {
        e.preventDefault();
        this.phoneError = 'Only numbers are allowed';
      }
    },
    onPhoneInput() {
      this.form.phone_number = this.form.phone_number.replace(/\D/g, '').slice(0, 10);
      if (this.form.phone_number.length === 0) {
        this.phoneError = '';
      } else if (this.form.phone_number.length < 10) {
        this.phoneError = `${10 - this.form.phone_number.length} more digit(s) needed`;
      } else {
        this.phoneError = '';
      }
    },
    async updateUser() {
      if (this.form.phone_number.length !== 10) {
        this.phoneError = 'Phone number must be exactly 10 digits';
        return;
      }
      this.phoneError = '';

      if (this.locationDisplay.trim() && !this.locationDetected) {
        try {
          const { lat, lon } = await this.getCoordsFromPlace(this.locationDisplay);
          this.form.location_coords = `${lat},${lon}`;
          this.form.location = this.locationDisplay;
        } catch {
          // keep existing coords if geocoding fails
        }
      }

      try {
        const response = await axios.patch(
          `${backendUrl}/api/auth/users/${this.user.id}`,
          this.form,
          { withCredentials: true }
        );
        this.successMessage = 'Profile updated successfully. To see the changes, log out';
        this.errorMessage = '';
        localStorage.setItem('userLocation', this.form.location);
        localStorage.setItem('userLocationCoords', this.form.location_coords);
        this.$emit('profile-updated', response.data.user);
      } catch (err) {
        this.errorMessage = err.response?.data?.error || 'Failed to update profile';
        this.successMessage = '';
      }
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    async detectLocation() {
      if (!navigator.geolocation) {
        this.locationError = 'Geolocation not supported on this device';
        return;
      }
      navigator.geolocation.getCurrentPosition(
        async pos => {
          const lat = pos.coords.latitude;
          const lon = pos.coords.longitude;
          this.form.location_coords = `${lat},${lon}`;

          // reverse geocode to get human-readable address
          try {
            const resp = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
            );
            const data = await resp.json();
            const address = data.display_name || `${lat},${lon}`;
            this.form.location = address;
            this.locationDisplay = address;  // for EditUserDashboard
            this.manualPlace = address;      // for SignupPage
          } catch {
            this.form.location = `${lat},${lon}`;
            this.locationDisplay = `${lat},${lon}`;
            this.manualPlace = `${lat},${lon}`;
          }

          this.locationDetected = true;
          this.autoLocation = { lat, lon };
          this.locationError = '';
        },
        err => {
          this.locationError = 'Could not detect location. Try entering manually.';
          console.error('Location error:', err);
        }
      );
    },
    onLocationInput() {
      this.form.location = this.locationDisplay;
      this.locationDetected = false;
    },
    async getCoordsFromPlace(place) {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(place)}&format=json&limit=1`
      );
      const results = await resp.json();
      if (!results.length) throw new Error('Place not found');
      const { lat, lon } = results[0];
      return { lat, lon };
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    // restore location if not on user prop
    if (!this.form.location) {
      this.form.location = localStorage.getItem('userLocation') || '';
      this.locationDisplay = this.form.location;
      this.form.location_coords = localStorage.getItem('userLocationCoords') || '';
    }
  },
  beforeUnmount() { 
    window.removeEventListener('resize', this.handleResize); 
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

.edit-user-dashboard {
  min-height: 100vh;
  width: 100%;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-color: #f5ece0;
}

.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 20px 0 24px;
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
  margin-top: 8px; margin-left: 7px;
}

.page-sub {
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
  margin-top: 2px;
}

.container {
  max-width: 760px;
  margin: 0 auto;
  padding: 20px 24px 40px;
}

.content {
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #f0e0ff;
  padding: 34px 32px;
}

.form-group { margin-bottom: 14px; }

.form-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 5px;
}

.form-input,
select.form-input {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  background: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
  font-family: inherit;
}

.form-input:focus,
select.form-input:focus,
.phone-input:focus {
  border-color: #ba71ff;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(186, 113, 255, 0.15);
}

.form-input:disabled {
  background: #f0f0f0;
  color: #bbb;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

.phone-input {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  background: #fffbe8;
  outline: none;
  transition: all 0.2s ease;
  color: #1a1a1a;
  font-family: inherit;
}

.phone-error {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #c0392b;
  background: #fde8e8;
  border-radius: 8px;
  padding: 6px 10px;
}

.location-btn {
  width: 100%;
  padding: 9px 13px;
  border: 1.5px solid #f0e0ff;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 700;
  background: #fffbe8;
  color: #4a1070;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  font-family: inherit;
}

.location-btn:hover {
  background: #eac7ff;
  border-color: #ba71ff;
}

.location-success {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #155724;
}

.location-error {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #721c24;
}

.update-button {
  width: 100%;
  padding: 11px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 10px;
  background: #eac7ff;
  color: #4a1070;
  font-family: inherit;
}

.update-button:hover { background: #ba71ff; color: #fff; }
.update-button:active { transform: scale(0.98); }

.update-button[type="button"] {
  background: #fffbe8;
  color: #888;
  border: 1.5px solid #f0e0ff;
  box-shadow: none;
}

.update-button[type="button"]:hover {
  background: #f5f0d8;
  color: #555;
}

.success,
.error {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
}

.success { background: #d4edda; color: #155724; }
.error   { background: #f8d7da; color: #721c24; }

.calendar-button-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
}

.calendar-button {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #fffbe8, #fff8d3);
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;
}

.calendar-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ffe876, #fffbe8);
  box-shadow: 0 6px 16px rgba(30,58,138,0.25);
}

.logout-now-btn {
  width: 100%;
  padding: 11px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  margin-top: 8px;
  background: #fde8e8;
  color: #991b1b;
  font-family: inherit;
}

.logout-now-btn:hover { background: #f8b4b4; }
.logout-now-btn:active { transform: scale(0.98); }

/* ── Desktop: 2 column grid ── */
@media (min-width: 600px) {
  form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 28px;
  }

  .form-group:has(.location-btn),
  .update-button,
  .success,
  .error {
    grid-column: 1 / -1;
  }

  .form-input,
  select.form-input,
  .phone-input { padding: 11px 15px; font-size: 15px; border-radius: 13px; }
  .location-btn { padding: 11px 15px; font-size: 15px; border-radius: 13px; }
  .form-label { font-size: 12px; margin-bottom: 6px; }
  .form-group { margin-bottom: 56px; }
  .update-button { padding: 13px; font-size: 15px; }
}

/* ── Mobile ── */
@media (max-width: 480px) {
  .page-header { padding: 20px 16px 0 46px; }
  .container { padding: 12px 12px 16px; }
  .content { padding: 10px 12px; }
  .form-group { margin-bottom: 8px; }
  .form-label { font-size: 9px; margin-bottom: 3px; }
  .form-input,
  select.form-input,
  .phone-input { padding: 6px 9px; font-size: 11px; border-radius: 8px; }
  .location-btn { padding: 6px 9px; font-size: 11px; border-radius: 8px; }
  .update-button { padding: 8px; font-size: 11px; margin-top: 6px; }
  .success, .error { font-size: 11px; padding: 6px 9px; }
  .logout-now-btn { padding: 8px; font-size: 11px; }
}
</style>